import React from 'react';
import {
  VictoryChart,
  VictoryStack,
  VictoryBar,
  VictoryAxis,
  VictoryTooltip
} from 'victory';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { useIntl, FormattedMessage } from 'react-intl';
import { SelectChangeEvent } from '@mui/material/Select';
import { getFormattedDuration } from 'modules/notes/common/components/Timer/useFormattedDuration';
import { useMessages } from 'modules/intl';
import SourceDropDown from './SourceDropDown';
import { useChartStyles } from './hooks/useChartStyles';
import SourceByTimeChartSkeleton from './SourceByTimeChartSkeleton';
import SourceByTimeChartNoDataFound from './SourceByTimeChartNoData';
import { DataValueProp } from './UserSourceByTimeChart';
import { DropdownLabel } from './util';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    padding: `${theme.spacing(2)} ${theme.spacing(2.5)}`
  },
  heading: {
    color: theme.palette.text.secondary
  },

  chartContainer: {
    paddingTop: theme.spacing(1)
  }
}));

const MyCustomFlyout: React.FC<any> = ({
  x,
  y,
  value
}: {
  x: number;
  y: number;
  value: string;
}) => (
  <foreignObject x={x - 40} y={y - 55} width="90" height="50">
    <Tooltip title={value} open={true}>
      <span />
    </Tooltip>
  </foreignObject>
);

type SourceByTimeChartProps = {
  dataValues: DataValueProp;
  isDisabled: boolean;
  isMetricsByCollectors: boolean;
  selectvalue: string;
  onSelectValueChange: (event: SelectChangeEvent) => void;
};
const DropdownLabelName = [
  `insights.timeBreakdownChart.${DropdownLabel.collectors}`,
  `insights.timeBreakdownChart.${DropdownLabel.categories}`
];
const SourceByTimeChartFC: React.FC<SourceByTimeChartProps> = ({
  dataValues,
  isDisabled,
  isMetricsByCollectors,
  selectvalue,
  onSelectValueChange
}: SourceByTimeChartProps) => {
  const theme = useTheme();

  const style = useChartStyles(theme);

  const { formatMessage } = useIntl();

  const classes = useStyles({ isMetricsByCollectors });
  const optionNames = useMessages(DropdownLabelName);
  const dropDownOptions = [
    {
      id: DropdownLabel.collectors,
      name:
        optionNames[`insights.timeBreakdownChart.${DropdownLabel.collectors}`]
    },
    {
      id: DropdownLabel.categories,
      name:
        optionNames[`insights.timeBreakdownChart.${DropdownLabel.categories}`]
    }
  ];

  return (
    <Grid container>
      <Grid container item>
        <Grid
          item
          xs={12}
          container
          alignItems={'center'}
          justifyContent="space-between"
        >
          <Typography variant="h6" className={classes.heading}>
            <FormattedMessage id="insights.timeBreakdownChart.title" />
          </Typography>
          <SourceDropDown
            value={selectvalue}
            onChange={onSelectValueChange}
            disabled={isDisabled}
            options={dropDownOptions}
          />
        </Grid>
      </Grid>
      {dataValues.loading ? (
        <SourceByTimeChartSkeleton />
      ) : (
        <>
          {!dataValues.isEmpty && (
            <>
              <Grid
                container
                item
                className={classes.chartContainer}
                data-testid={'source-by-time-chart'}
              >
                <VictoryChart
                  height={style.VictoryChart.height}
                  domainPadding={style.VictoryChart.domainPadding}
                  padding={style.VictoryChart.padding}
                  animate={{
                    duration: 1000,
                    easing: 'bounce'
                  }}
                >
                  <VictoryStack>
                    {dataValues.dataset.map((data, i) => {
                      const formattedTime = getFormattedDuration({
                        formatMessage,
                        duration: data[0].value ?? 0,
                        needsMask: true
                      });
                      return (
                        <VictoryBar
                          labels={() => ''}
                          labelComponent={
                            <VictoryTooltip
                              flyoutPadding={10}
                              flyoutComponent={
                                <MyCustomFlyout
                                  value={`${data[0].name}: ${formattedTime}`}
                                />
                              }
                            />
                          }
                          horizontal
                          data={data}
                          key={i}
                          barWidth={style.VictoryBar.width}
                          style={{
                            data: { fill: data[0].color }
                          }}
                          events={[
                            {
                              target: 'data',
                              eventHandlers: {
                                onMouseOver: () => {
                                  return [
                                    {
                                      target: 'data',
                                      mutation: (props) => {
                                        return {
                                          style: {
                                            // eslint-disable-next-line react/prop-types
                                            fill: props.datum.color,
                                            stroke:
                                              theme.palette.information
                                                .background,
                                            strokeWidth: 1,
                                            cursor: 'pointer'
                                          }
                                        };
                                      }
                                    },
                                    {
                                      target: 'labels',
                                      mutation: () => ({ active: true })
                                    }
                                  ];
                                },
                                onMouseLeave: () => {
                                  return [
                                    {
                                      target: 'data',
                                      mutation: (props) => {
                                        return {
                                          style: {
                                            // eslint-disable-next-line react/prop-types
                                            fill: props.datum.color
                                          }
                                        };
                                      }
                                    },
                                    {
                                      target: 'labels',
                                      mutation: () => ({ active: undefined })
                                    }
                                  ];
                                }
                              }
                            }
                          ]}
                        />
                      );
                    })}
                  </VictoryStack>
                  <VictoryAxis style={style.VictoryAxis} />
                </VictoryChart>
              </Grid>
            </>
          )}
          {dataValues.isEmpty && <SourceByTimeChartNoDataFound />}
        </>
      )}
    </Grid>
  );
};

export default React.memo(SourceByTimeChartFC);
